import LegalLayout from '../components/legal/LegalLayout';
import Seo from '../components/Seo';

const Section = ({ title, children }) => (
  <section id={title.toLowerCase().replace(/[^a-z0-9]+/g, '-')} className="scroll-mt-24">
    <h2 className="font-['Josefin_Sans'] text-3xl sm:text-4xl font-bold text-[#B5FF00] mb-3 tracking-tight leading-none">
      {title}
    </h2>
    <div className="space-y-5">{children}</div>
  </section>
);
const Sub = ({ children }) => (
  <h3 className="font-['Josefin_Sans'] text-xl text-white mt-5 mb-2 tracking-tight">{children}</h3>
);
const P = ({ children }) => <p className="text-zinc-300 leading-relaxed">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-none space-y-1.5 ml-1">
    {items.map((item) => (
      <li key={item} className="text-zinc-300 flex gap-3">
        <span className="text-[#B5FF00] flex-shrink-0">·</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Anchors = ({ items }) => (
  <nav className="border border-zinc-800 bg-[#0A0A0A] p-5 mb-12" aria-label="Sections">
    <p className="font-mono text-xs text-zinc-500 tracking-widest mb-3">JUMP TO</p>
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
      {items.map(({ id, label }) => (
        <li key={id}>
          <a href={`#${id}`} className="text-zinc-300 hover:text-[#B5FF00] text-sm transition-colors">
            → {label}
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

const TermsPage = () => (
  <LegalLayout title="TERMS & POLICIES" lastUpdated="June 2026">
    <Seo title="Terms of Service" description="Terms and conditions for using Unight — the underground events discovery and ticketing platform for the UK and India." path="/terms" />
    <P>
      This document covers Unight&apos;s full set of platform policies. Use the index below to jump to a specific
      section.
    </P>
    <Anchors items={[
      { id: 'terms-conditions', label: 'Terms & Conditions' },
      { id: 'community-guidelines', label: 'Community Guidelines' },
      { id: 'safety-policy', label: 'Safety Policy' },
      { id: 'prohibited-items-venue-entry-policy', label: 'Prohibited Items & Venue Entry' },
      { id: 'india-dpdp-compliance', label: 'India DPDP Compliance' },
    ]} />

    {/* ---------- TERMS & CONDITIONS ---------- */}
    <Section title="TERMS & CONDITIONS">
      <Sub>1. Acceptance of Terms</Sub>
      <P>
        By accessing or using Unight, you agree to be bound by these Terms and Conditions. If you do not agree with
        any part of these Terms, you must not use the platform.
      </P>

      <Sub>2. Platform Services</Sub>
      <P>
        Unight provides event discovery, ticketing, venue listings, organizer tools, and community features for
        nightlife and music events.
      </P>

      <Sub>3. User Eligibility</Sub>
      <UL items={[
        'Users must be at least 18 years old or the legal age required to attend an event in their jurisdiction.',
        'Users are responsible for ensuring compliance with local laws regarding attendance at events.',
      ]} />

      <Sub>4. User Accounts</Sub>
      <UL items={[
        'Users are responsible for maintaining the confidentiality of their account credentials.',
        'Unight reserves the right to suspend or terminate accounts involved in fraudulent, abusive, or illegal activities.',
      ]} />

      <Sub>5. Ticket Purchases</Sub>
      <UL items={[
        'All ticket purchases are subject to availability.',
        'Tickets may not be resold unless expressly permitted by the event organizer.',
        'Each ticket is valid only for the event specified.',
        'Unight reserves the right to cancel tickets obtained through fraud, chargebacks, or unauthorized resale.',
      ]} />

      <Sub>6. Refund Policy</Sub>
      <UL items={[
        "Refunds are subject to the event organizer's refund policy.",
        'Service fees may be non-refundable.',
        'Cancelled events may qualify for full or partial refunds depending on organizer terms.',
      ]} />

      <Sub>7. Event Changes</Sub>
      <P>Event organizers may modify event dates, times, venues, lineups, or schedules. Unight is not liable for organizer-made changes.</P>

      <Sub>8. User Conduct</Sub>
      <P>Users must not:</P>
      <UL items={[
        'Engage in harassment or abusive behavior.',
        'Upload unlawful content.',
        'Attempt unauthorized access to the platform.',
        'Use the platform for fraudulent purposes.',
      ]} />

      <Sub>9. Community Features</Sub>
      <P>Users are responsible for content posted within community areas. Unight may remove content that violates laws or community standards.</P>

      <Sub>10. Intellectual Property</Sub>
      <P>All trademarks, branding, software, designs, and content on Unight remain the property of Unight or its licensors.</P>

      <Sub>11. Limitation of Liability</Sub>
      <P>
        Unight acts as a platform connecting users and organizers. Unight is not responsible for injuries, losses,
        cancellations, venue issues, or organizer actions.
      </P>

      <Sub>12. Privacy</Sub>
      <P>Personal information is processed in accordance with the Unight Privacy Policy.</P>

      <Sub>13. Termination</Sub>
      <P>Unight may suspend or terminate access for violations of these Terms.</P>

      <Sub>14. Governing Law</Sub>
      <P>
        These Terms shall be governed by the laws applicable to the user&apos;s jurisdiction or the jurisdiction in
        which Unight operates.
      </P>

      <Sub>15. Contact</Sub>
      <P>
        For legal or support inquiries:{' '}
        <a className="text-[#B5FF00] hover:underline" href="mailto:support@unighttickets.com">
          support@unighttickets.com
        </a>
      </P>
    </Section>

    {/* ---------- COMMUNITY GUIDELINES ---------- */}
    <Section title="COMMUNITY GUIDELINES">
      <P>
        At Unight, we aim to create a safe, inclusive, and enjoyable environment for event attendees, organizers,
        artists, venues, and community members. By using the platform, attending events, or participating in
        community features, you agree to follow these Community Guidelines.
      </P>

      <Sub>1. Respect Others</Sub>
      <P>All users must treat others with respect and dignity. The following behavior is not permitted:</P>
      <UL items={[
        'Harassment or bullying',
        'Hate speech or discriminatory language',
        'Threats or intimidation',
        'Personal attacks',
        'Stalking or unwanted contact',
        'Sexual harassment',
        'Offensive or abusive conduct',
      ]} />

      <Sub>2. Authenticity</Sub>
      <P>Users must provide accurate information when creating accounts, purchasing tickets, or organizing events. You may not:</P>
      <UL items={[
        'Create fake accounts',
        'Impersonate individuals or organizations',
        'Misrepresent your identity',
        'Mislead attendees regarding events',
      ]} />

      <Sub>3. Illegal Activities</Sub>
      <P>Users must not use Unight to promote or engage in illegal activities, including:</P>
      <UL items={[
        'Drug distribution',
        'Fraud',
        'Theft',
        'Violence',
        'Human trafficking',
        'Counterfeit ticket sales',
        'Money laundering activities',
      ]} />

      <Sub>4. Content Standards</Sub>
      <P>Content shared on Unight must not contain:</P>
      <UL items={[
        'Hate speech',
        'Violent threats',
        'Pornographic material',
        'Graphic violence',
        'Illegal content',
        'Copyright infringement',
        'Defamatory statements',
      ]} />

      <Sub>5. Event Conduct</Sub>
      <P>When attending events, users are expected to:</P>
      <UL items={[
        'Follow venue rules',
        'Respect staff and security personnel',
        'Follow emergency instructions',
        'Respect other attendees',
        'Avoid disruptive behavior',
      ]} />

      <Sub>6. Enforcement</Sub>
      <P>Violations may result in:</P>
      <UL items={[
        'Content removal',
        'Account suspension',
        'Permanent account termination',
        'Event access restrictions',
        'Reporting to law enforcement authorities where required',
      ]} />

      <Sub>7. Reporting</Sub>
      <P>
        Users can report violations through{' '}
        <a className="text-[#B5FF00] hover:underline" href="mailto:support@unighttickets.com">
          support@unighttickets.com
        </a>.
      </P>
    </Section>

    {/* ---------- SAFETY POLICY ---------- */}
    <Section title="SAFETY POLICY">
      <P>The safety of attendees, organizers, artists, venues, and staff is a priority for Unight.</P>

      <Sub>1. Attendee Safety</Sub>
      <P>Attendees should:</P>
      <UL items={[
        'Use valid tickets purchased through authorized channels',
        'Follow all venue instructions',
        'Remain aware of surroundings',
        'Report suspicious behavior',
        'Respect venue capacity limits',
        'Follow emergency procedures when instructed',
      ]} />

      <Sub>2. Organizer Responsibilities</Sub>
      <P>Event organizers must:</P>
      <UL items={[
        'Maintain all required permits and licenses',
        'Ensure venue compliance',
        'Provide appropriate security personnel where necessary',
        'Maintain emergency response procedures',
        'Comply with local safety regulations',
      ]} />

      <Sub>3. Venue Responsibilities</Sub>
      <P>Venues are expected to:</P>
      <UL items={[
        'Maintain safe premises',
        'Comply with fire and safety regulations',
        'Clearly mark emergency exits',
        'Maintain crowd-control measures',
        'Provide emergency response support when required',
      ]} />

      <Sub>4. Alcohol &amp; Substance Safety</Sub>
      <P>
        Attendees are expected to consume alcohol responsibly. Unight does not encourage excessive alcohol consumption
        or the use of illegal substances.
      </P>

      <Sub>5. Incident Reporting</Sub>
      <P>Users may report:</P>
      <UL items={[
        'Harassment',
        'Physical threats',
        'Fraudulent activity',
        'Security concerns',
        'Unsafe venue conditions',
      ]} />
      <P>
        Reports should be submitted to{' '}
        <a className="text-[#B5FF00] hover:underline" href="mailto:support@unighttickets.com">
          support@unighttickets.com
        </a>.
      </P>

      <Sub>6. Emergency Situations</Sub>
      <P>
        In emergencies, attendees should immediately contact venue security, emergency services, or local authorities
        before contacting Unight.
      </P>
    </Section>

    {/* ---------- PROHIBITED ITEMS ---------- */}
    <Section title="PROHIBITED ITEMS & VENUE ENTRY POLICY">
      <P>
        To ensure the safety and enjoyment of all attendees, certain items and behaviors are prohibited at events
        listed on Unight.
      </P>

      <Sub>1. Prohibited Items</Sub>
      <P>The following items may not be brought into participating venues or events:</P>
      <UL items={[
        'Illegal drugs', 'Controlled substances without legal authorization', 'Firearms', 'Weapons of any kind',
        'Ammunition', 'Explosives', 'Fireworks', 'Knives or sharp objects', 'Pepper spray or chemical agents',
        'Laser pointers', 'Dangerous tools', 'Drones', 'Glass containers', 'Outside alcohol',
        'Unauthorized professional recording equipment', 'Counterfeit tickets',
      ]} />

      <Sub>2. Restricted Items</Sub>
      <P>Individual venues may restrict additional items including:</P>
      <UL items={[
        'Large backpacks', 'Suitcases', 'Selfie sticks', 'Professional cameras', 'Tripods',
        'Flags or banners', 'Food and beverages',
      ]} />

      <Sub>3. Security Screening</Sub>
      <P>As a condition of entry:</P>
      <UL items={[
        'Security personnel may inspect bags and personal belongings.',
        'Metal detectors or security checks may be conducted.',
        'Refusal to cooperate may result in denied entry.',
      ]} />

      <Sub>4. Entry Requirements</Sub>
      <P>Attendees may be required to provide:</P>
      <UL items={[
        'Valid government-issued identification',
        'Valid event ticket',
        'Age verification documentation where applicable',
      ]} />

      <Sub>5. Refusal of Entry</Sub>
      <P>Entry may be refused if an attendee:</P>
      <UL items={[
        'Appears intoxicated',
        'Possesses prohibited items',
        'Presents fraudulent tickets',
        'Engages in threatening behavior',
        'Violates venue policies',
      ]} />

      <Sub>6. Removal from Event</Sub>
      <P>Attendees may be removed without refund for:</P>
      <UL items={[
        'Dangerous behavior',
        'Harassment',
        'Violence',
        'Illegal activities',
        'Repeated violations of venue rules',
      ]} />
    </Section>

    {/* ---------- DPDP ---------- */}
    <Section title="INDIA DPDP COMPLIANCE">
      <P>
        This notice explains how Unight complies with the Digital Personal Data Protection Act, 2023 of India.
      </P>

      <Sub>1. Collection of Personal Data</Sub>
      <P>Unight collects only the personal data necessary to provide platform services, including:</P>
      <UL items={[
        'Name', 'Email address', 'Phone number', 'Date of birth',
        'Ticket purchase information', 'Account details', 'Device and usage information',
      ]} />

      <Sub>2. Purpose of Processing</Sub>
      <P>Personal data may be processed to:</P>
      <UL items={[
        'Create and manage accounts',
        'Process ticket purchases',
        'Verify user identity',
        'Prevent fraud',
        'Improve platform functionality',
        'Provide customer support',
        'Meet legal obligations',
      ]} />

      <Sub>3. Consent</Sub>
      <P>
        Where required by law, personal data will be processed only after obtaining valid user consent. Users may
        withdraw consent at any time, subject to legal or contractual limitations.
      </P>

      <Sub>4. User Rights</Sub>
      <P>Users may:</P>
      <UL items={[
        'Request access to personal data',
        'Request correction of inaccurate information',
        'Request deletion of personal information',
        'Withdraw consent',
        'Seek grievance redressal',
      ]} />

      <Sub>5. Data Security</Sub>
      <P>
        Unight maintains reasonable security safeguards to protect personal data from unauthorized access, disclosure,
        misuse, alteration, or destruction.
      </P>

      <Sub>6. Data Retention</Sub>
      <P>
        Personal information will be retained only as long as reasonably necessary to fulfill the purposes for which
        it was collected or as required by law.
      </P>

      <Sub>7. Cross-Border Data Transfers</Sub>
      <P>
        Personal data may be transferred and processed in jurisdictions permitted under applicable Indian laws and
        regulations.
      </P>

      <Sub>8. Grievance Officer</Sub>
      <P>
        Users may contact Unight regarding privacy concerns, complaints, or requests at:{' '}
        <a className="text-[#B5FF00] hover:underline" href="mailto:privacy@unighttickets.com">
          privacy@unighttickets.com
        </a>.
      </P>

      <Sub>9. Policy Updates</Sub>
      <P>
        Unight may update this notice periodically. Updated versions will be published on the platform and become
        effective upon publication.
      </P>
    </Section>
  </LegalLayout>
);

export default TermsPage;
